'use client';

import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Paper, 
  Title, 
  TextInput, 
  Group, 
  Text,
  Badge,
  ActionIcon,
  Menu,
  Loader,
  Modal,
  Stack,
  Select
} from '@mantine/core';
import { IconSearch, IconDotsVertical, IconEdit, IconTrash, IconUserOff, IconUserCheck } from '@tabler/icons-react';
import { getAllUsers, updateUserProfile, deleteUser } from '../services/firestore';
import { UserProfile } from '../types';

interface UserAdminDashboardProps {
  onError?: (error: Error) => void;
}

export const UserAdminDashboard: React.FC<UserAdminDashboardProps> = ({ 
  onError 
}) => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const allUsers = await getAllUsers();
        setUsers(allUsers);
        setFilteredUsers(allUsers);
      } catch (error) {
        console.error('Error loading users:', error);
        onError?.(error as Error);
      } finally {
        setLoading(false);
      }
    };
    
    loadUsers();
  }, [onError]);
  
  useEffect(() => {
    if (searchQuery) {
      const filtered = users.filter(user => 
        user.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchQuery, users]);
  
  const handleEditUser = (user: UserProfile) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };
  
  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    try {
      await updateUserProfile(editingUser.uid, editingUser);
      
      // Update local state
      setUsers(users.map(u => u.uid === editingUser.uid ? editingUser : u));
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating user:', error);
      onError?.(error as Error);
    }
  };
  
  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      await deleteUser(userId);
      
      // Update local state
      setUsers(users.filter(u => u.uid !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      onError?.(error as Error);
    }
  };
  
  const handleToggleUserStatus = async (user: UserProfile) => {
    const newStatus = user.disabled ? false : true;
    
    try {
      await updateUserProfile(user.uid, { disabled: newStatus });
      
      // Update local state
      setUsers(users.map(u => u.uid === user.uid ? {...u, disabled: newStatus} : u));
    } catch (error) {
      console.error('Error updating user status:', error);
      onError?.(error as Error);
    }
  };
  
  if (loading) {
    return <Loader />;
  }
  
  return (
    <Paper radius="md" p="xl" withBorder>
      <Group justify="apart" mb="md">
        <Title order={2}>User Management</Title>
        <TextInput
          placeholder="Search users..."
          leftSection={<IconSearch size={14} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: 300 }}
        />
      </Group>
      
      <Text size="sm" color="dimmed">
        {filteredUsers.length} users found
      </Text>
      
      <Table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>Organizations</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((user) => (
            <tr key={user.uid}>
              <td>{user.displayName || 'No name'}</td>
              <td>{user.email}</td>
              <td>{user.role || 'User'}</td>
              <td>
                <Badge 
                  color={user.disabled ? 'red' : 'green'}
                >
                  {user.disabled ? 'Disabled' : 'Active'}
                </Badge>
              </td>
              <td>{user.organizations?.length || 0}</td>
              <td>
                <Menu position="bottom-end">
                  <Menu.Target>
                    <ActionIcon>
                      <IconDotsVertical size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  
                  <Menu.Dropdown>
                    <Menu.Item 
                      leftSection={<IconEdit size={16} />}
                      onClick={() => handleEditUser(user)}
                    >
                      Edit User
                    </Menu.Item>
                    
                    <Menu.Item 
                      leftSection={user.disabled ? <IconUserCheck size={16} /> : <IconUserOff size={16} />}
                      onClick={() => handleToggleUserStatus(user)}
                      color={user.disabled ? 'green' : 'orange'}
                    >
                      {user.disabled ? 'Enable User' : 'Disable User'}
                    </Menu.Item>
                    
                    <Menu.Item 
                      leftSection={<IconTrash size={16} />}
                      onClick={() => handleDeleteUser(user.uid)}
                      color="red"
                    >
                      Delete User
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      
      {/* Edit User Modal */}
      <Modal
        opened={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit User"
      >
        {editingUser && (
          <Stack>
            <TextInput
              label="Display Name"
              value={editingUser.displayName || ''}
              onChange={(e) => setEditingUser({...editingUser, displayName: e.target.value})}
            />
            
            <TextInput
              label="Email"
              value={editingUser.email}
              disabled
            />
            
            <TextInput
              label="Phone Number"
              value={editingUser.phoneNumber || ''}
              onChange={(e) => setEditingUser({...editingUser, phoneNumber: e.target.value})}
            />
            
            <Select
              label="Role"
              value={editingUser.role || 'user'}
              onChange={(value) => setEditingUser({...editingUser, role: value || 'user'})}
              data={[
                { value: 'user', label: 'User' },
                { value: 'admin', label: 'Admin' },
                { value: 'moderator', label: 'Moderator' }
              ]}
            />
            
            <Group justify="right" mt="md">
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveUser}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Paper>
  );
}; 